import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard } from '../platform/_ui';
import * as api from '../api/org-integrations.api.js';

/* Integrations owns: Catalog, Installed Apps, Marketplace, Connection Status.
   Developer primitives (API Keys, Webhooks, OAuth) live in Developer Center. */

const CAT_ICON = {
  Productivity: 'Briefcase',
  Messaging:    'MessageSquare',
  Automation:   'Zap',
  CRM:          'Users',
  Identity:     'Shield',
};

const CAT_TONE = {
  Productivity: 'blue',
  Messaging:    'purple',
  Automation:   'amber',
  CRM:          'green',
  Identity:     'red',
};

const IntegrationsSkeleton = () => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
    {Array.from({ length: 6 }, (_, i) => (
      <div key={i} className="nx-cc-chart-card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--nx-surface-alt)', opacity: 0.4, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 14, borderRadius: 4, background: 'var(--nx-surface-alt)', opacity: 0.4, marginBottom: 6, width: '60%' }} />
            <div style={{ height: 20, borderRadius: 20, background: 'var(--nx-surface-alt)', opacity: 0.4, width: '40%' }} />
          </div>
        </div>
        <div style={{ height: 11, borderRadius: 4, background: 'var(--nx-surface-alt)', opacity: 0.4, marginBottom: 4, width: '90%' }} />
        <div style={{ height: 11, borderRadius: 4, background: 'var(--nx-surface-alt)', opacity: 0.4, marginBottom: 12, width: '70%' }} />
        <div style={{ borderTop: '1px solid var(--nx-divider)', paddingTop: 10 }}>
          <div style={{ height: 28, borderRadius: 6, background: 'var(--nx-surface-alt)', opacity: 0.4, width: 80 }} />
        </div>
      </div>
    ))}
  </div>
);

export default function OrgIntegrations({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const toast      = useToast();
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [connecting, setConnecting]     = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [catFilter, setCatFilter]       = useState('');

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');

  const org = data.state.organizations.find(o => o.id === orgId);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await api.getIntegrations(orgId);
      setIntegrations(d);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load integrations' });
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async (item) => {
    setConnecting(item.id);
    try {
      await api.connectIntegration(orgId, item.id);
      setIntegrations(prev => ({
        ...prev,
        catalog: (prev?.catalog || []).map(c => c.id === item.id ? { ...c, connected: true } : c),
      }));
      toast({ tone: 'success', icon: 'Check', title: `${item.name} connected` });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: `Failed to connect ${item.name}` });
    } finally { setConnecting(null); }
  };

  const handleDisconnect = async (item) => {
    setDisconnecting(item.id);
    try {
      await api.disconnectIntegration(orgId, item.id);
      setIntegrations(prev => ({
        ...prev,
        catalog: (prev?.catalog || []).map(c => c.id === item.id ? { ...c, connected: false } : c),
      }));
      toast({ tone: 'success', icon: 'Check', title: `${item.name} disconnected` });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: `Failed to disconnect ${item.name}` });
    } finally { setDisconnecting(null); }
  };

  if (!org) return null;

  const catalog        = integrations?.catalog ?? [];
  const connectedCount = catalog.filter(c => c.connected).length;
  const categories     = [...new Set(catalog.map(c => c.category).filter(Boolean))];

  const renderCatalog = () => {
    if (loading) return <IntegrationsSkeleton />;
    if (catalog.length === 0) {
      return (
        <div className="nx-cc-chart-card" style={{ padding: 40, textAlign: 'center' }}>
          <NexusIcon name="Plug" size={24} style={{ color: 'var(--nx-text-muted)', marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nx-text)', marginBottom: 6 }}>No integrations available</div>
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Integration catalog is empty. Check back later or contact support.</div>
        </div>
      );
    }
    const filtered = catFilter ? catalog.filter(c => c.category === catFilter) : catalog;
    return (
      <>
        {categories.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
            {['', ...categories].map(cat => (
              <button key={cat || '__all'} type="button" onClick={() => setCatFilter(cat)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: catFilter === cat ? 600 : 500, cursor: 'pointer', border: `1px solid ${catFilter === cat ? 'var(--nx-accent)' : 'var(--nx-border)'}`, background: catFilter === cat ? 'var(--nx-accent)' : 'var(--nx-surface-alt)', color: catFilter === cat ? '#fff' : 'var(--nx-text)' }}>
                {cat ? <NexusIcon name={CAT_ICON[cat] || 'Grid'} size={11} /> : null}
                {cat || 'All'}
              </button>
            ))}
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="nx-empty" style={{ padding: '32px 0' }}>
            <NexusIcon name="SearchX" size={20} /><span>No integrations in this category.</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
            {filtered.map(item => {
              const catIcon = CAT_ICON[item.category] ?? 'Plug';
              const catTone = CAT_TONE[item.category] ?? 'gray';
              return (
                <div key={item.id} className="nx-cc-chart-card" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <NexusIcon name={catIcon} size={18} style={{ color: 'var(--nx-text-muted)' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)' }}>{item.name}</div>
                      <Pill tone={catTone}>{item.category}</Pill>
                    </div>
                  </div>
                  {item.description && (
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 12, lineHeight: 1.5 }}>{item.description}</div>
                  )}
                  <div style={{ borderTop: '1px solid var(--nx-divider)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {item.connected ? (
                      <>
                        <Pill tone="green">Connected</Pill>
                        <Btn variant="ghost" size="sm" icon="Unlink" disabled={disconnecting === item.id} onClick={() => handleDisconnect(item)}>
                          {disconnecting === item.id ? 'Disconnecting…' : 'Disconnect'}
                        </Btn>
                      </>
                    ) : (
                      <Btn variant="primary" size="sm" disabled={connecting === item.id} onClick={() => handleConnect(item)}>
                        {connecting === item.id ? 'Connecting…' : 'Connect'}
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Integrations"
        sub={`Connect ${org.name} with external services`}
        back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
        actions={<Btn variant="ghost" icon="Code" onClick={() => navigate(`/organization/${orgId}/developer`)}>Developer Center</Btn>}
      />

      <div className="nx-cc-stat-grid nx-kpi-3">
        <StatCard icon="Plug"        label="Available"   value={catalog.length}    sub="in catalog"          color="blue"   />
        <StatCard icon="CheckCircle" label="Connected"   value={connectedCount}    sub="active integrations" color="green"  />
        <StatCard icon="Grid"        label="Categories"  value={categories.length} sub="integration types"   color="purple" />
      </div>

      {renderCatalog()}
    </div>
  );
}
